import { Card, MonoLabel, Skeleton, cn, focusRing } from '@/design-system';
import type { OrderDocument } from '@/entities/order';
import { CardTitle, ErrorState } from '@/shared/ui';

import { dossierCopy } from '../copy';

export interface DocumentsCardProps {
    documents: readonly OrderDocument[];
    loading?: boolean;
    error?: unknown;
    onRetry: () => void;
}

/**
 * Wat er bij de keuring hoort: rapportversies, facturen en bijlagen. Een
 * document met een bestand opent via zijn kortlevende link in een nieuw
 * tabblad; zonder bestand staat er waarom ("Wordt gegenereerd").
 */
export function DocumentsCard({
    documents,
    loading = false,
    error = null,
    onRetry,
}: DocumentsCardProps) {
    return (
        <Card padding="md" id="documenten" className="scroll-mt-4">
            <CardTitle className="mb-3">{dossierCopy.documentsTitle}</CardTitle>

            {error ? (
                <ErrorState error={error} title={dossierCopy.documentsError} onRetry={onRetry} />
            ) : loading ? (
                <Skeleton height={74} radius={8} pulse label={dossierCopy.documentsLoading} />
            ) : documents.length === 0 ? (
                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {dossierCopy.documentsEmpty}
                </p>
            ) : (
                <ul className="m-0 grid list-none gap-[10px] p-0 [grid-template-columns:repeat(auto-fit,minmax(132px,1fr))]">
                    {documents.map((document) => (
                        <li key={document.uuid} className="min-w-0">
                            <DocumentTile document={document} />
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

function DocumentTile({ document }: { document: OrderDocument }) {
    const body = (
        <>
            {document.thumbnailUrl ? (
                <img
                    src={document.thumbnailUrl}
                    alt=""
                    className="block h-[74px] w-full bg-[var(--surface-subtle)] object-cover"
                />
            ) : (
                <Skeleton variant="hatch" height={74} radius={0} />
            )}
            <div className="px-[10px] py-[9px]">
                <MonoLabel size={9} tone="muted" as="div">
                    {dossierCopy.documentKinds[document.kind]}
                </MonoLabel>
                <div className="mt-[2px] truncate text-[12px] font-medium text-[var(--text-primary)]">
                    {document.name}
                </div>
                <MonoLabel
                    size={10}
                    variant="value"
                    tone="muted"
                    as="div"
                    className="mt-[2px] truncate"
                >
                    {document.meta ?? document.statusLabel ?? dossierCopy.unknown}
                </MonoLabel>
            </div>
        </>
    );

    const frame =
        'block overflow-hidden rounded-[8px] border border-[var(--border-default)] bg-[var(--surface-card)]';

    return document.downloadUrl ? (
        <a
            href={document.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={dossierCopy.documentOpen(document.name)}
            className={cn(frame, 'hover:border-[var(--accent)]', focusRing)}
        >
            {body}
        </a>
    ) : (
        <div className={frame}>{body}</div>
    );
}
